'use client'

import type { JSX, MouseEvent } from 'react'

import { Button } from '../design-system'
import type { ButtonProps } from '../design-system/button'

type Calendly = { initPopupWidget: (options: { url: string }) => void }

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- only an interface merges into Window
  interface Window {
    Calendly?: Calendly
  }
}

// `setupCalendlyPopup` (`app.js:1204-1233`) injects exactly these two files.
const STYLESHEET = 'https://assets.calendly.com/assets/external/widget.css'
const SCRIPT = 'https://assets.calendly.com/assets/external/widget.js'

// Module-level, so every demo button on the page shares one load. Cleared on failure, so a later
// click can retry a network blip instead of being stuck with a rejected promise.
let widget: Promise<Calendly> | undefined

function loadCalendly(): Promise<Calendly> {
  widget ??= new Promise<Calendly>((resolve, reject) => {
    const script = Object.assign(document.createElement('script'), { src: SCRIPT, async: true })
    script.addEventListener('load', () =>
      window.Calendly ? resolve(window.Calendly) : reject(new Error('no Calendly')),
    )
    script.addEventListener('error', () => reject(new Error('Calendly failed to load')))
    document.head.append(Object.assign(document.createElement('link'), { rel: 'stylesheet', href: STYLESHEET }), script)
  }).catch((error: unknown) => {
    widget = undefined
    throw error
  })

  return widget
}

// Hover and focus are intent too: starting the load there usually has the widget ready by the click.
function warm(): void {
  loadCalendly().catch(() => undefined)
}

function openPopup(event: MouseEvent<HTMLAnchorElement>): void {
  // A modified click is someone asking for a new tab or window: leave it to the browser, as production does.
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

  // Until the widget has loaded, the click is an ordinary link to the booking page (`app.js:1225`),
  // and it starts the load for next time. Waiting for the load instead would leave a dead button
  // whenever the request stalls rather than fails.
  if (!window.Calendly) {
    warm()
    return
  }

  event.preventDefault()
  const url = new URL(event.currentTarget.href)
  url.searchParams.set('hide_gdpr_banner', '1')
  // Compact calendar-only popup: no details column, so no misaligned Privacy Policy footer (S20-1268).
  url.searchParams.set('hide_event_type_details', '1')
  window.Calendly.initPopupWidget({ url: url.toString() })
}

type CalendlyButtonProps = Extract<ButtonProps, { href: string }>

/**
 * A demo `<Button>` that opens Calendly's booking popup instead of leaving the page. It stays a real
 * link to the booking page, so it works with no JS, and a modified click still opens a new tab.
 *
 * Nothing Calendly loads with the page: its CSS and JS are requested on first intent (hover, focus
 * or click), once. Production loads both on every page that has a demo link. Hover and focus usually
 * have the widget ready by the click; a click that beats the load just follows the link.
 */
export default function CalendlyButton({ onClick, ...props }: CalendlyButtonProps): JSX.Element {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.(event)
    openPopup(event)
  }

  return <Button {...props} onClick={handleClick} onPointerEnter={warm} onFocus={warm} />
}
